import type { Direction } from "./types";

const OPPOSITE: Record<Direction, Direction> = {
  up: "down",
  down: "up",
  left: "right",
  right: "left",
};

/** Whether `direction` is the exact 180-degree reversal of `reference` - the same rule `InputQueue.push()` uses internally, exported so callers that need to classify a direction WITHOUT going through the queue (e.g. a pre-run "waiting for first input" gate, where a same-direction press must count as valid start intent even though `push()` itself would silently no-op it) can apply the identical reversal check. */
export function isOppositeDirection(direction: Direction, reference: Direction): boolean {
  return OPPOSITE[reference] === direction;
}

/**
 * PR32 (touch fairness pass): the outcome of `InputQueue.push()` - richer
 * than a plain accepted/rejected boolean so a caller that needs to know
 * WHY a direction wasn't queued (not just whether) can react differently.
 * `TouchInput` is the one caller that currently does - see its own doc
 * for why only `"illegal-reverse"` gets special treatment (holding its
 * gesture's anchor instead of re-anchoring); every other caller only
 * needs the accepted/rejected distinction and can ignore the specific
 * reason.
 */
export type DirectionPushResult = "queued" | "same-direction" | "illegal-reverse" | "queue-full";

/**
 * Buffers a small number of direction changes so that quick, legal
 * key presses / swipes performed between two grid ticks are not lost.
 *
 * Validation is done against the *last queued* direction (not just the
 * snake's current direction), so a player can never queue a 180-degree
 * reversal even across multiple buffered inputs.
 */
export class InputQueue {
  private queue: Direction[] = [];

  constructor(private readonly maxSize: number) {}

  /** The direction the next queued (or current) move should be validated against. */
  private referenceDirection(current: Direction): Direction {
    return this.queue.length > 0 ? this.queue[this.queue.length - 1] : current;
  }

  /**
   * PR31 audit note: when the queue is already at `maxSize` (2, currently
   * `GameConfig.input.queueMax`), a third legal direction is silently
   * dropped - deliberately, not a bug. Two buffered turns is already the
   * maximum a player could reasonably pre-plan before the first one has
   * even executed; a third would be executed far enough after the input
   * that produced it (waiting behind two full movement ticks) that
   * honoring it risks the classic "queued input executing long after the
   * player intended it" failure mode this project explicitly wants to
   * avoid, rather than fixing a real missed-input problem. The player's
   * NEXT swipe after the queue drains is unaffected - this only ever
   * discards an excess third input attempted while two are already
   * pending, never a first or second one.
   */
  push(direction: Direction, currentDirection: Direction): DirectionPushResult {
    if (this.queue.length >= this.maxSize) {
      return "queue-full";
    }
    const reference = this.referenceDirection(currentDirection);
    if (direction === reference) {
      return "same-direction"; // no-op, already heading that way
    }
    if (direction === OPPOSITE[reference]) {
      return "illegal-reverse"; // illegal instant reversal
    }
    this.queue.push(direction);
    return "queued";
  }

  /** Pops the next direction to apply, or falls back to the current one. */
  consume(currentDirection: Direction): Direction {
    const next = this.queue.shift();
    return next ?? currentDirection;
  }

  clear(): void {
    this.queue = [];
  }
}
